import { useEffect, useState } from 'react';
import {
  fetchPlans,
  isContactOnlyPlan,
  subscribe,
  validateCoupon,
  type CouponValidation,
  type PlanInfo,
} from '../services/api';
import { useT, type TFunction } from '../i18n';

interface PricingProps {
  onNavigate?: (view: 'signup' | 'billing' | 'login') => void;
  isLoggedIn?: boolean;
  userRole?: string;
}

// Roles com permissao de billing na org (espelha MANAGE_ROLES do
// backend — o subscribe com cupom e 403 para outros perfis).
const COUPON_ROLES = ['super_user', 'parceiro', 'admin', 'owner'];

// Issue #593: ordem de exibicao dos 4 planos (Free | Single | Team |
// Enterprise). 'pro' fica no meio por compatibilidade de transicao — se a
// API ainda responder com a key legada, o card renderiza na posicao certa
// em vez de sumir ou ir parar no fim.
const PLAN_ORDER = ['free', 'single', 'pro', 'team', 'enterprise'];

// Destaque "Mais popular": Team no contrato novo (#592); 'pro' mantido para
// a janela de transicao em que o backend ainda serve o plano antigo.
const POPULAR_PLANS = new Set(['team', 'pro']);

// E-mail comercial do plano contact-only (Enterprise). O subscribe/checkout
// e rejeitado pelo backend para esses planos — o CTA aqui e SEMPRE mailto.
const SALES_EMAIL = 'vendas@auranota.app';

function planRank(planKey: string): number {
  const idx = PLAN_ORDER.indexOf(planKey);
  // Planos desconhecidos (futuros) vao para o fim, na ordem da API.
  return idx === -1 ? PLAN_ORDER.length : idx;
}

// Regra contact-only compartilhada: ver isContactOnlyPlan em
// services/api.ts (flag da API ou key 'enterprise' na janela #592).
const isContactOnly = isContactOnlyPlan;

// Seat de contador (derivado — nao ha flag dedicada na API): plano
// multi-usuario com mais de 1 seat OU seats ilimitados (max_users < 0,
// convencao Enterprise). Sem isso, um plano ilimitado exibiria ✗ na
// linha "usuario contador" — inconsistente (M1 do review #595).
function hasAccountantSeat(p: PlanInfo): boolean {
  const max = p.features?.max_users ?? 1;
  return Boolean(p.features?.multi_user) && (max < 0 || max > 1);
}

// Celula da tabela comparativa: booleano (✓/✗) ou texto ja resolvido
// (ex.: "300", "Ilimitado").
type ComparisonCell = boolean | string;

interface ComparisonRow {
  labelKey: string;
  cell: (p: PlanInfo, t: TFunction) => ComparisonCell;
}

// Issue #593: matriz comparativa. Linhas que existem em PLAN_FEATURES sao
// derivadas da API (/api/billing/plans — mesma fonte dos cards). As demais
// NAO existem no contrato do backend e ficam documentadas aqui:
//   - "recurso contador": derivado — exige multi-usuario com >1 seat
//     (Team/Enterprise). Nao ha flag dedicada na API.
//   - "captura por e-mail + upload manual": canal de entrada disponivel em
//     todo plano que tem `upload` (todos hoje) — nao ha gating por plano.
//   - "onboarding assistido": oferta comercial contact-only (Enterprise).
const COMPARISON_ROWS: ComparisonRow[] = [
  {
    labelKey: 'comparison.row.docs',
    cell: (p, t) =>
      p.docs_per_month <= 0 ? t('comparison.unlimited') : String(p.docs_per_month),
  },
  {
    labelKey: 'comparison.row.users',
    cell: (p, t) =>
      (p.features?.max_users ?? 1) < 0
        ? t('comparison.unlimited')
        : String(p.features?.max_users ?? 1),
  },
  {
    // Derivado: seat de contador — multi-usuario com >1 seat ou ilimitado
    // (max_users < 0). Regra em hasAccountantSeat().
    labelKey: 'comparison.row.accountant',
    cell: (p) => hasAccountantSeat(p),
  },
  {
    // Canal de entrada (e-mail dedicado captura-*@ + upload manual): livre
    // em todo plano com upload habilitado — hoje, todos.
    labelKey: 'comparison.row.emailUpload',
    cell: (p) => Boolean(p.features?.upload),
  },
  { labelKey: 'comparison.row.exportJson', cell: (p) => Boolean(p.features?.export_json) },
  { labelKey: 'comparison.row.exportCsv', cell: (p) => Boolean(p.features?.export_csv) },
  { labelKey: 'comparison.row.multiUser', cell: (p) => Boolean(p.features?.multi_user) },
  { labelKey: 'comparison.row.audit', cell: (p) => Boolean(p.features?.audit) },
  { labelKey: 'comparison.row.apiAccess', cell: (p) => Boolean(p.features?.api_access) },
  { labelKey: 'comparison.row.prioritySupport', cell: (p) => Boolean(p.features?.priority_support) },
  {
    // Onboarding assistido: so planos contact-only (Enterprise).
    labelKey: 'comparison.row.onboarding',
    cell: (p) => isContactOnly(p),
  },
];

export default function Pricing({ onNavigate, isLoggedIn, userRole }: PricingProps) {
  const t = useT();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{
    qr: string | null;
    plan: string;
    courtesy: boolean;
    amount: number;
  } | null>(null);
  // Issue #544: cupom de desconto no checkout (apenas usuarios logados —
  // /api/coupons/validate exige autenticacao).
  const [couponInput, setCouponInput] = useState('');
  const [couponByPlan, setCouponByPlan] = useState<Record<string, CouponValidation>>({});
  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  // Security review #544: campo de cupom so para roles de billing —
  // o backend rejeita subscribe com cupom para member (403).
  const canUseCoupon =
    !!isLoggedIn && COUPON_ROLES.includes(userRole ?? '');

  useEffect(() => {
    fetchPlans()
      .then((resp) => {
        // Issue #593: ordena Free | Single | Team | Enterprise; keys
        // desconhecidas vao para o fim sem quebrar o grid.
        const sorted = [...resp.plans].sort(
          (a, b) => planRank(a.plan) - planRank(b.plan),
        );
        // Transicao #592: 'pro' e 'single' sao o mesmo tier (R$99). Se a
        // API responder com ambos na janela de migracao, renderiza so
        // 'single' — a pagina deve ter exatamente 4 cards.
        const hasSingle = sorted.some((p) => p.plan === 'single');
        setPlans(
          hasSingle ? sorted.filter((p) => p.plan !== 'pro') : sorted,
        );
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error'))
      .finally(() => setLoading(false));
  }, []);

  // Valida o cupom contra cada plano pago com checkout self-service —
  // a elegibilidade e por plano (eligible_plans). Planos contact-only
  // (Enterprise) nao entram: nao ha subscribe para eles (#592/#593).
  async function handleApplyCoupon() {
    const code = couponInput.trim();
    if (!code) return;
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      const paidPlans = plans.filter((p) => p.price > 0 && !isContactOnly(p));
      const results = await Promise.all(
        paidPlans.map(
          async (p) => [p.plan, await validateCoupon(code, p.plan)] as const,
        ),
      );
      const map: Record<string, CouponValidation> = {};
      let anyValid = false;
      for (const [plan, v] of results) {
        map[plan] = v;
        if (v.valid) anyValid = true;
      }
      if (!anyValid) {
        const reason = results[0]?.[1]?.reason ?? 'invalid_plan';
        setCouponError(t(`pricing.coupon.error.${reason}`));
        setCouponByPlan({});
      } else {
        setCouponByPlan(map);
      }
    } catch (err) {
      setCouponError(err instanceof Error ? err.message : 'Error');
      setCouponByPlan({});
    } finally {
      setApplyingCoupon(false);
    }
  }

  function handleRemoveCoupon() {
    setCouponByPlan({});
    setCouponInput('');
    setCouponError(null);
  }

  async function handleSubscribe(planKey: string) {
    if (planKey === 'free') {
      onNavigate?.('signup');
      return;
    }
    if (!isLoggedIn) {
      onNavigate?.('signup');
      return;
    }
    setSubscribing(planKey);
    setError(null);
    try {
      const applied = couponByPlan[planKey];
      const resp = await subscribe(
        planKey,
        'pix',
        applied?.valid ? applied.code : undefined,
      );
      setPixData({
        qr: resp.qr_code_base64,
        plan: planKey,
        courtesy: resp.courtesy,
        amount: resp.invoice.amount,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubscribing(null);
    }
  }

  if (loading) return <p className="text-brand-600">{t('app.loading')}</p>;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-brand-900">{t('pricing.title')}</h1>
        <p className="mt-2 text-brand-600">{t('pricing.subtitle')}</p>
      </div>

      {error && (
        <p className="mx-auto max-w-2xl rounded-md bg-red-50 px-4 py-3 text-center text-sm text-red-700">{error}</p>
      )}

      {/* Issue #544: campo "Tem um cupom?" — logado + role de billing
          (member nao aplica cupom: subscribe e 403 no backend). */}
      {canUseCoupon && !pixData && (
        <div className="mx-auto max-w-2xl rounded-lg border border-brand-200 bg-white p-4 shadow-sm">
          <label htmlFor="coupon-input" className="text-sm font-medium text-brand-800">
            {t('pricing.coupon.label')}
          </label>
          <div className="mt-2 flex gap-2">
            <input
              id="coupon-input"
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value)}
              placeholder={t('pricing.coupon.placeholder')}
              className="flex-1 rounded-md border border-brand-300 px-3 py-2 text-sm uppercase focus:border-brand-500 focus:outline-none"
              disabled={applyingCoupon}
            />
            {Object.keys(couponByPlan).length > 0 ? (
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('pricing.coupon.remove')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={applyingCoupon || !couponInput.trim()}
                className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60"
              >
                {applyingCoupon ? t('pricing.coupon.applying') : t('pricing.coupon.apply')}
              </button>
            )}
          </div>
          {couponError && (
            <p className="mt-2 text-sm text-red-600">{couponError}</p>
          )}
          {Object.values(couponByPlan).some((v) => v.valid) && (
            <p className="mt-2 text-sm text-green-700">{t('pricing.coupon.applied')}</p>
          )}
        </div>
      )}

      {pixData && (
        <div className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-6 text-center shadow-sm">
          {pixData.courtesy || !pixData.qr ? (
            <>
              <h2 className="text-xl font-bold text-green-700">{t('pricing.courtesyTitle')}</h2>
              <p className="mt-1 text-sm text-brand-600">{t('pricing.courtesyBody')}</p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-brand-800">{t('pricing.pixTitle')}</h2>
              <p className="mt-1 text-sm text-brand-600">
                {t('pricing.pixInstructions')} R$ {pixData.amount.toFixed(2)}
              </p>
              <img
                src={`data:image/png;base64,${pixData.qr}`}
                alt="PIX QR Code"
                className="mx-auto mt-4 h-48 w-48"
              />
            </>
          )}
          <button
            type="button"
            onClick={() => onNavigate?.('billing')}
            className="mt-4 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            {t('pricing.goToBilling')}
          </button>
        </div>
      )}

      {/* Issue #593: 4 planos — Free | Single | Team (popular) | Enterprise
          (contact-only). */}
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {plans.map((p) => {
          const isPopular = POPULAR_PLANS.has(p.plan);
          const contactOnly = isContactOnly(p);
          // Defesa: plano sem bloco `features` na resposta (janela de
          // transicao do backend #592) renderiza tudo como indisponivel
          // em vez de quebrar a pagina.
          const feats = p.features ?? ({} as PlanInfo['features']);
          // Issue #544: preco com desconto quando ha cupom valido p/ o plano.
          const applied = couponByPlan[p.plan];
          const discounted =
            applied?.valid && applied.discounted_price !== undefined
              ? applied.discounted_price
              : null;
          const priceLabel = contactOnly
            ? t('pricing.unlimited')
            : p.docs_per_month <= 0
              ? t('pricing.unlimited')
              : p.price === 0
                ? t('pricing.free')
                : `R$ ${p.price.toFixed(0)}/${t('pricing.month')}`;
          const maxUsers = feats.max_users ?? 1;
          const usersLabel =
            maxUsers < 0
              ? t('pricing.users.unlimited')
              : maxUsers === 1
                ? t('pricing.users.one')
                : t('pricing.users.upTo', { count: maxUsers });
          // Seat de contador: ver hasAccountantSeat() — cobre max_users
          // < 0 (ilimitado) alem de > 1.
          const accountantSeat = hasAccountantSeat(p);
          const mailtoHref = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(
            t('pricing.enterpriseMailSubject'),
          )}`;
          return (
            <div
              key={p.plan}
              className={`relative flex flex-col rounded-lg border bg-white p-6 shadow-sm ${
                isPopular ? 'border-brand-600 ring-2 ring-brand-600' : 'border-brand-200'
              }`}
            >
              {isPopular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white">
                  {t('pricing.popular')}
                </span>
              )}
              <h2 className="text-xl font-bold text-brand-900">{p.name}</h2>
              {discounted !== null ? (
                <p className="mt-2">
                  <span className="text-xl font-bold text-brand-400 line-through">{priceLabel}</span>
                  <span className="ml-2 text-3xl font-bold text-green-700">
                    R$ {discounted.toFixed(2)}/{t('pricing.month')}
                  </span>
                </p>
              ) : (
                <p className="mt-2 text-3xl font-bold text-brand-800">{priceLabel}</p>
              )}
              <p className="mt-1 text-sm text-brand-600">
                {p.docs_per_month <= 0
                  ? t('pricing.unlimitedDocs')
                  : t('pricing.docsPerMonth', { count: p.docs_per_month })}
              </p>
              <p className="mt-1 text-sm text-brand-600">{usersLabel}</p>

              <ul className="mt-4 space-y-2 text-sm text-brand-700">
                <li className={feats.upload ? '' : 'text-brand-300 line-through'}>
                  {feats.upload ? '✓' : '✗'} {t('pricing.feat.upload')}
                </li>
                <li className={feats.upload ? '' : 'text-brand-300 line-through'}>
                  {feats.upload ? '✓' : '✗'} {t('pricing.feat.emailCapture')}
                </li>
                <li className={feats.export_json ? '' : 'text-brand-300 line-through'}>
                  {feats.export_json ? '✓' : '✗'} {t('pricing.feat.exportJson')}
                </li>
                <li className={feats.export_csv ? '' : 'text-brand-300 line-through'}>
                  {feats.export_csv ? '✓' : '✗'} {t('pricing.feat.exportCsv')}
                </li>
                <li className={feats.multi_user ? '' : 'text-brand-300 line-through'}>
                  {feats.multi_user ? '✓' : '✗'} {t('pricing.feat.multiUser')}
                </li>
                <li className={accountantSeat ? '' : 'text-brand-300 line-through'}>
                  {accountantSeat ? '✓' : '✗'} {t('pricing.feat.accountant')}
                </li>
                <li className={feats.audit ? '' : 'text-brand-300 line-through'}>
                  {feats.audit ? '✓' : '✗'} {t('pricing.feat.audit')}
                </li>
                <li className={feats.api_access ? '' : 'text-brand-300 line-through'}>
                  {feats.api_access ? '✓' : '✗'} {t('pricing.feat.apiAccess')}
                </li>
                <li className={feats.priority_support ? '' : 'text-brand-300 line-through'}>
                  {feats.priority_support ? '✓' : '✗'} {t('pricing.feat.prioritySupport')}
                </li>
                <li className={contactOnly ? '' : 'text-brand-300 line-through'}>
                  {contactOnly ? '✓' : '✗'} {t('pricing.feat.onboarding')}
                </li>
              </ul>

              {contactOnly ? (
                // Issue #593: Enterprise e contact-only — CTA mailto com
                // subject pre-preenchido. NUNCA subscribe/checkout (o
                // backend rejeita com 400/422).
                <a
                  href={mailtoHref}
                  className="mt-6 block w-full rounded-md border border-brand-300 px-4 py-2 text-center text-sm font-medium text-brand-700 hover:bg-brand-50"
                >
                  {t('pricing.ctaEnterprise')}
                </a>
              ) : (
                <button
                  type="button"
                  disabled={subscribing === p.plan}
                  onClick={() => handleSubscribe(p.plan)}
                  className={`mt-6 w-full rounded-md px-4 py-2 text-sm font-medium ${
                    isPopular
                      ? 'bg-brand-700 text-white hover:bg-brand-800'
                      : 'border border-brand-300 text-brand-700 hover:bg-brand-50'
                  } disabled:opacity-60`}
                >
                  {subscribing === p.plan
                    ? t('pricing.processing')
                    : p.plan === 'free'
                      ? t('pricing.ctaFree')
                      : t('pricing.ctaPaid')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Issue #593: tabela comparativa — mesma fonte da API para as linhas
          cobertas por PLAN_FEATURES; linhas sem contrato na API estao
          documentadas em COMPARISON_ROWS acima. */}
      {plans.length > 0 && (
        <section aria-labelledby="comparison-title" className="pt-4">
          <h2
            id="comparison-title"
            className="text-center text-2xl font-bold text-brand-900"
          >
            {t('comparison.title')}
          </h2>
          <p className="mt-2 text-center text-brand-600">{t('comparison.subtitle')}</p>
          <div className="mt-6 overflow-x-auto rounded-lg border border-brand-200 bg-white shadow-sm">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-brand-200 bg-brand-50">
                  <th scope="col" className="px-4 py-3 text-left font-semibold text-brand-900">
                    {t('comparison.feature')}
                  </th>
                  {plans.map((p) => (
                    <th
                      key={p.plan}
                      scope="col"
                      className={`px-4 py-3 text-center font-semibold ${
                        POPULAR_PLANS.has(p.plan) ? 'text-brand-800' : 'text-brand-700'
                      }`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, idx) => (
                  <tr
                    key={row.labelKey}
                    className={idx % 2 === 0 ? 'bg-white' : 'bg-brand-50/50'}
                  >
                    <th scope="row" className="px-4 py-2.5 text-left font-medium text-brand-800">
                      {t(row.labelKey)}
                    </th>
                    {plans.map((p) => {
                      const value = row.cell(p, t);
                      return (
                        <td
                          key={p.plan}
                          className={`px-4 py-2.5 text-center ${
                            value === false ? 'text-brand-300' : 'text-brand-700'
                          }`}
                        >
                          {typeof value === 'boolean' ? (
                            <span
                              role="img"
                              aria-label={value ? t('common.yes') : t('common.no')}
                            >
                              {value ? '✓' : '✗'}
                            </span>
                          ) : (
                            value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
